import { z } from 'zod';
import { CurrencyCodeSchema, DateOnlySchema, IdSchema, ListQuerySchema, MoneyAmountSchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
/**
 * Estimates (specification 8.20). Xactimate stays the estimating system of record; the platform
 * keeps the agreed summary of each estimate revision on the job file, the artifacts as private
 * documents, and an explicit link to the project and claim it prices. Trade-neutral: a roofing
 * tenant's estimates come from somewhere else and carry the same summary.
 */
export const EstimateSourceSchema = z.enum(['xactimate', 'manual', 'other']);
export const EstimateStatusSchema = z.enum([
    'draft',
    'submitted',
    'approved',
    'revised',
    'superseded',
]);
/** The agreed summary fields: the totals on every Xactimate estimate summary, in this order. */
export const ESTIMATE_SUMMARY_FIELDS = [
    'lineItemTotal',
    'materialSalesTax',
    'overheadAmount',
    'profitAmount',
    'replacementCostValue',
    'depreciationAmount',
    'actualCashValue',
    'deductibleAmount',
    'netClaimAmount',
];
export const EstimateSummaryFieldSchema = z.enum(ESTIMATE_SUMMARY_FIELDS);
export const EstimateSummarySchema = z.strictObject({
    lineItemTotal: MoneyAmountSchema.nullable(),
    materialSalesTax: MoneyAmountSchema.nullable(),
    overheadAmount: MoneyAmountSchema.nullable(),
    profitAmount: MoneyAmountSchema.nullable(),
    replacementCostValue: MoneyAmountSchema.nullable(),
    depreciationAmount: MoneyAmountSchema.nullable(),
    actualCashValue: MoneyAmountSchema.nullable(),
    deductibleAmount: MoneyAmountSchema.nullable(),
    netClaimAmount: MoneyAmountSchema.nullable(),
});
/** A subtotal by the estimating system's category (for Xactimate, the category code such as WTR). */
export const EstimateCategoryTotalSchema = z.strictObject({
    code: z.string().trim().min(1).max(40),
    description: z.string().trim().max(200).nullable(),
    amount: MoneyAmountSchema,
    lineItemCount: z.number().int().nonnegative().nullable(),
});
export const EstimateSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    jobFileId: IdSchema,
    projectId: IdSchema.nullable(),
    insuranceClaimId: IdSchema.nullable(),
    /** Sequence within the job file; a later revision replaces an earlier one only through the status model. */
    revision: z.number().int().positive(),
    source: EstimateSourceSchema,
    status: EstimateStatusSchema,
    label: z.string(),
    /** The estimating system's own identifier for the estimate, typed by the user. */
    externalReference: z.string().nullable(),
    estimateDate: DateOnlySchema.nullable(),
    preparedBy: z.string().nullable(),
    currency: CurrencyCodeSchema,
    summary: EstimateSummarySchema,
    lineItemCount: z.number().int().nonnegative().nullable(),
    categoryTotals: z.array(EstimateCategoryTotalSchema),
    notes: z.string().nullable(),
    importId: IdSchema.nullable(),
    createdByMembershipId: IdSchema,
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
const EditableEstimateFields = {
    label: z.string().trim().min(1).max(200),
    externalReference: z.string().trim().max(120).nullable(),
    estimateDate: DateOnlySchema.nullable(),
    preparedBy: z.string().trim().max(200).nullable(),
    summary: EstimateSummarySchema.partial(),
    lineItemCount: z.number().int().nonnegative().nullable(),
    categoryTotals: z.array(EstimateCategoryTotalSchema).max(200),
    notes: z.string().trim().max(2000).nullable(),
    projectId: IdSchema.nullable(),
    insuranceClaimId: IdSchema.nullable(),
};
export const CreateEstimateRequestSchema = z.strictObject({
    jobFileId: IdSchema,
    source: EstimateSourceSchema.default('manual'),
    currency: CurrencyCodeSchema.optional(),
    label: EditableEstimateFields.label,
    externalReference: EditableEstimateFields.externalReference.default(null),
    estimateDate: EditableEstimateFields.estimateDate.default(null),
    preparedBy: EditableEstimateFields.preparedBy.default(null),
    summary: EditableEstimateFields.summary.default({}),
    lineItemCount: EditableEstimateFields.lineItemCount.default(null),
    categoryTotals: EditableEstimateFields.categoryTotals.default([]),
    notes: EditableEstimateFields.notes.default(null),
    projectId: EditableEstimateFields.projectId.default(null),
    insuranceClaimId: EditableEstimateFields.insuranceClaimId.default(null),
});
/** Editable while the estimate is a draft; the status model governs everything after. */
export const UpdateEstimateRequestSchema = z.strictObject({
    version: VersionSchema,
    label: EditableEstimateFields.label.optional(),
    externalReference: EditableEstimateFields.externalReference.optional(),
    estimateDate: EditableEstimateFields.estimateDate.optional(),
    preparedBy: EditableEstimateFields.preparedBy.optional(),
    summary: EditableEstimateFields.summary.optional(),
    lineItemCount: EditableEstimateFields.lineItemCount.optional(),
    categoryTotals: EditableEstimateFields.categoryTotals.optional(),
    notes: EditableEstimateFields.notes.optional(),
    projectId: EditableEstimateFields.projectId.optional(),
    insuranceClaimId: EditableEstimateFields.insuranceClaimId.optional(),
});
export const EstimateStatusChangeRequestSchema = z.strictObject({
    version: VersionSchema,
    status: EstimateStatusSchema,
    reason: z.string().trim().max(500).nullable().default(null),
});
export const EstimateListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    status: EstimateStatusSchema.optional(),
});
export const EstimateListSchema = z.strictObject({
    items: z.array(EstimateSchema),
    page: PageInfoSchema,
});
/**
 * A reviewed import of the agreed summary fields from a supported Xactimate Excel export. The
 * upload is a document on the job file; the worker parses it into a preview; a person reviews
 * the preview and confirms, which creates the estimate revision. Nothing is imported partially.
 */
export const EstimateImportStatusSchema = z.enum([
    'pending',
    'parsing',
    'ready',
    'failed',
    'confirmed',
    'canceled',
]);
export const EstimateImportIssueCodeSchema = z.enum([
    'no_summary_found',
    'no_line_items',
    'ambiguous_field',
    'line_item_total_differs',
]);
export const EstimateImportIssueSchema = z.strictObject({
    code: EstimateImportIssueCodeSchema,
    severity: z.enum(['warning', 'notice']),
    message: z.string(),
    field: EstimateSummaryFieldSchema.optional(),
});
export const EstimateImportDetectedFieldSchema = z.strictObject({
    field: EstimateSummaryFieldSchema,
    amount: MoneyAmountSchema,
    /** The label as it appears in the workbook and the cell the amount was read from. */
    label: z.string(),
    cell: z.string(),
});
export const EstimateImportPreviewSchema = z.strictObject({
    profile: z.literal('xactimate-excel-v1'),
    worksheets: z.array(z.string()),
    fields: z.array(EstimateImportDetectedFieldSchema),
    categoryTotals: z.array(EstimateCategoryTotalSchema),
    lineItemCount: z.number().int().nonnegative().nullable(),
    lineItemsWorksheet: z.string().nullable(),
    issues: z.array(EstimateImportIssueSchema),
});
export const EstimateImportSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    jobFileId: IdSchema,
    documentId: IdSchema,
    status: EstimateImportStatusSchema,
    preview: EstimateImportPreviewSchema.nullable(),
    failureCode: z.string().nullable(),
    estimateId: IdSchema.nullable(),
    requestedByMembershipId: IdSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateEstimateImportRequestSchema = z.strictObject({
    jobFileId: IdSchema,
    documentId: IdSchema,
});
/** What the reviewer adds or corrects before the preview becomes an estimate revision. */
export const ConfirmEstimateImportRequestSchema = z.strictObject({
    label: EditableEstimateFields.label,
    externalReference: EditableEstimateFields.externalReference.default(null),
    estimateDate: EditableEstimateFields.estimateDate.default(null),
    preparedBy: EditableEstimateFields.preparedBy.default(null),
    /** Overrides for detected fields; anything omitted keeps the detected amount. */
    summary: EditableEstimateFields.summary.default({}),
    notes: EditableEstimateFields.notes.default(null),
    projectId: EditableEstimateFields.projectId.default(null),
    insuranceClaimId: EditableEstimateFields.insuranceClaimId.default(null),
});
export const EstimateImportListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    status: EstimateImportStatusSchema.optional(),
});
export const EstimateImportListSchema = z.strictObject({
    items: z.array(EstimateImportSchema),
    page: PageInfoSchema,
});
